import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import {
  clearFuelConsumption,
  getConsumptionMachines,
} from 'features/fuelManagement/fuelConsumptionSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { FuelPageSubtitle } from 'pages/FuelManagement/FuelManagement.styled'
import { formatGhs } from 'pages/FuelManagement/Shared/fuelFormatters'
import { mapFuelMachineLeague } from 'pages/FuelManagement/Shared/fuelApiMappers'
import {
  bandDeltaPct,
  bandLabelOf,
  bandStateOf,
  FUEL_LEAGUE_FOOTNOTE,
  FUEL_LEAGUE_SUBLINE,
  machineClassText,
  signedPctLabel,
  worstFuelMachines,
} from 'pages/FuelManagement/Shared/fuelConsumptionUtils'
import { Pagination } from 'components/Pagination/Pagination'
import { Flex, H1WithBack, PageFooter, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { RootState } from 'store'
import { IFuelMachine } from 'types/fuelConsumption.interface'
import { formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { ModePill, ProvenanceBadge } from './ProvenanceBadge'
import { LeagueFootnote, WorstCard, WorstName, WorstRate, WorstStrip, WorstSub } from './FuelMachineLeague.styled'

const BAND_COLOR = {
  over: tokens.status.danger.fg,
  in: tokens.status.success.fg,
  under: tokens.text.muted,
}

export const FuelMachineLeague = () => {
  const navigate = useNavigate()
  const [params, setParams] = useState<any>(getParams({ location }))
  const [classFilter, setClassFilter] = useState<'ALL' | 'MOBILE' | 'STATIC'>('ALL')
  const { machines } = useSelector((store: RootState) => store.fuelConsumption)

  useQueryPagination({
    fetchFunctions: [getConsumptionMachines],
    cleanUpFunctions: [clearFuelConsumption],
    params,
  })

  const mapped = useMemo(
    () => machines.data.map(mapFuelMachineLeague),
    [machines.data],
  )

  const worst = worstFuelMachines(mapped)
  const filtered = mapped.filter((m) =>
    classFilter === 'ALL'
      ? true
      : classFilter === 'MOBILE'
        ? m.machineClass === 'OPERATED_MOBILE'
        : m.machineClass === 'STATIC_PLANT',
  )

  const columns: IHonestTableColumn<IFuelMachine>[] = [
    {
      id: 'machine',
      header: 'MACHINE',
      sticky: true,
      minWidth: '14rem',
      cell: (item) => (
        <Flex column gap='0.25rem'>
          <Flex center gap='0.5rem'>
            <BodyText weight='bold'>{item.name}</BodyText>
            <ModePill mode={item.fuelDataMode} />
          </Flex>
          <OtherText grey>{`${machineClassText(item)} · ${item.model}`}</OtherText>
        </Flex>
      ),
    },
    {
      id: 'litres30d',
      header: '30D LITRES',
      align: 'right',
      cell: (item) => <BodyText weight='bold'>{`${formatNumbersForDisplay(item.litres30d)} L`}</BodyText>,
    },
    {
      id: 'avgRate',
      header: 'AVG L/H',
      minWidth: '11rem',
      cell: (item) => (
        <Flex center gap='0.5rem'>
          <BodyText weight='bold'>{item.avgRateLph}</BodyText>
          {item.avgProvenance && <ProvenanceBadge provenance={item.avgProvenance} />}
        </Flex>
      ),
    },
    {
      id: 'vsNominal',
      header: 'VS NOMINAL',
      align: 'right',
      cell: (item) => {
        const delta = bandDeltaPct(item.avgRateLph, item.nominalBurnRateLph)
        const state = bandStateOf(item.avgRateLph, item.nominalBurnRateLph)
        return (
          <BodyText weight='bold' color={BAND_COLOR[state]}>
            {signedPctLabel(delta)}
          </BodyText>
        )
      },
    },
    {
      id: 'cost30d',
      header: '30D FUEL COST',
      align: 'right',
      cell: (item) => <BodyText>{formatGhs(item.cost30dGhs ?? 0)}</BodyText>,
    },
    {
      id: 'flags',
      header: 'FLAGS',
      minWidth: '14rem',
      cell: (item) =>
        item.flags.length ? (
          <Flex center gap='0.5rem' wrap>
            {item.flags.map((f) => (
              <StatusPill key={f.kind} status={f.tone} dot={f.tone === 'danger'}>
                {f.label}
              </StatusPill>
            ))}
          </Flex>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack title='Machine fuel' arrow onClick={() => navigate('/fuel/dashboard')} />
        <FuelPageSubtitle>{FUEL_LEAGUE_SUBLINE}</FuelPageSubtitle>
        {machines.isLoading && !mapped.length ? (
          <NoData text='Loading machine fuel analytics…' />
        ) : !mapped.length ? (
          <NoData text='No machine fuel analytics yet — dispenses and telemetry will build the league here.' />
        ) : (
          <>
            <WorstStrip>
              {worst.map((m) => {
                const headline = m.avgRateLph
                const delta = bandDeltaPct(headline, m.nominalBurnRateLph)
                const state = bandStateOf(headline, m.nominalBurnRateLph)
                const topFlag = m.flags[0]
                return (
                  <WorstCard key={m.id} onClick={() => navigate(`/fuel/machines/${m.id}`)}>
                    <Flex between center gap='0.5rem'>
                      <WorstName>{m.name}</WorstName>
                      {topFlag ? (
                        <StatusPill status={topFlag.tone} dot={topFlag.tone === 'danger'}>
                          {topFlag.label}
                        </StatusPill>
                      ) : (
                        <StatusPill status='success'>IN BAND</StatusPill>
                      )}
                    </Flex>
                    <WorstRate $color={BAND_COLOR[state]}>{`${headline} L/h`}</WorstRate>
                    <WorstSub>
                      {`${signedPctLabel(delta)} ${bandLabelOf(headline, m.nominalBurnRateLph)} · nominal ${m.nominalBurnRateLph}`}
                    </WorstSub>
                  </WorstCard>
                )
              })}
            </WorstStrip>
            <Flex mb='16'>
              <TabsContainer>
                <Tabs active={classFilter === 'ALL'} onClick={() => setClassFilter('ALL')}>
                  All
                </Tabs>
                <Tabs active={classFilter === 'MOBILE'} onClick={() => setClassFilter('MOBILE')}>
                  Mobile
                </Tabs>
                <Tabs active={classFilter === 'STATIC'} onClick={() => setClassFilter('STATIC')}>
                  Static plant
                </Tabs>
              </TabsContainer>
            </Flex>
            <HonestTable
              columns={columns}
              data={filtered}
              keyField='id'
              emptyMessage='No machines in this class yet.'
              onClick={(item) => navigate(`/fuel/machines/${item.id}`)}
            />
            <LeagueFootnote>
              <OtherText color={tokens.text.muted}>{FUEL_LEAGUE_FOOTNOTE}</OtherText>
            </LeagueFootnote>
          </>
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams)}
          pageCount={machines.totalPages}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
